import { useEffect, useRef } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import QueryKey from "@app/constants/QueryKeys.ts";
import { SyncAction, SyncActionStatus } from "@app/models/SyncAction.ts";
import { useSyncService } from "@app/services/api/useSyncService.ts";

const POLLING_INTERVAL_MS = 5000;

const runningActions = (statuses: SyncActionStatus[] | undefined) =>
  new Set<SyncAction>(
    (statuses ?? []).filter((s) => s.running).map((s) => s.action),
  );

export const useSyncActions = () => {
  const { fetchSyncActions } = useSyncService();
  const queryClient = useQueryClient();
  const previouslyRunning = useRef<Set<SyncAction>>(new Set());

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [QueryKey.SYNC_ACTIONS],
    queryFn: fetchSyncActions,
    refetchInterval: (query) =>
      runningActions(query.state.data).size > 0 ? POLLING_INTERVAL_MS : false,
  });

  useEffect(() => {
    const running = runningActions(data);
    const someRunFinished = [...previouslyRunning.current].some(
      (action) => !running.has(action),
    );
    previouslyRunning.current = running;
    if (someRunFinished) {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.SYNC_RUNS] });
    }
  }, [data, queryClient]);

  return {
    syncActions: data ?? [],
    error,
    isLoading,
    refetch,
  };
};
